import styled from "styled-components";
import { Theme } from "../../atoms/theme";

export const TableContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  background: transparent;
  border-radius: ${Theme.radii.corner_radius_l};
  box-shadow: ${Theme.shadows.dropshadow_m};
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const TableWrapper = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${Theme.colors.neutrals_500};
  min-width: 100%;
`;

export const StyledThead = styled.thead`
  position: sticky;
  top: 0;
  background: ${Theme.colors.neutrals_500};
  z-index: 10;
  border-bottom: 2px solid ${Theme.colors.neutrals_400};
`;

export const StyledTh = styled.th<{ $canSort?: boolean }>`
  text-align: left;
  padding: 1rem;
  font-weight: 600;
  color: ${Theme.colors.neutrals_100};
  cursor: ${(props) => (props.$canSort ? "pointer" : "default")};
  user-select: none;
  white-space: nowrap;

  &:hover {
    color: ${(props) =>
      props.$canSort ? Theme.colors.primaries_000 : Theme.colors.neutrals_100};
  }

  input[type="checkbox"] {
    vertical-align: middle;
    margin: 0;
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const SortIcon = styled.span<{ $direction?: "asc" | "desc" | false }>`
  display: inline-flex;
  align-items: center;
  opacity: ${(props) => (props.$direction ? 1 : 0.3)};
  font-size: 0.75rem;
`;

export const StyledTbody = styled.tbody`
  overflow-y: auto;
`;

export const StyledTr = styled.tr<{
  $clickable?: boolean;
  $selected?: boolean;
}>`
  border-bottom: 1px solid ${Theme.colors.neutrals_400};
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  background: ${(props) =>
    props.$selected ? Theme.colors.primaries_300 : "transparent"};
  transition: background 0.15s ease;
  height: 64px;

  &:hover {
    background: ${(props) =>
      props.$clickable ? Theme.colors.neutrals_400 : "transparent"};
  }
`;

export const StyledTd = styled.td`
  padding: 1rem;
  color: ${Theme.colors.neutrals_100};
  vertical-align: middle;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CheckboxCell = styled(StyledTd)`
  width: 48px;
  padding: 0.5rem;
  text-align: center;

  input[type="checkbox"] {
    vertical-align: middle;
    margin: 0;
  }
`;

export const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-top: 1px solid ${Theme.colors.neutrals_400};
  background: ${Theme.colors.neutrals_500};
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid ${Theme.colors.neutrals_400};
  border-radius: ${Theme.radii.corner_radius_s};
  background: ${Theme.colors.neutrals_500};
  color: ${Theme.colors.neutrals_100};
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: ${Theme.colors.neutrals_400};
    border-color: ${Theme.colors.primaries_000};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PageInfo = styled.span`
  font-size: 0.875rem;
  color: ${Theme.colors.neutrals_200};
  white-space: nowrap;
`;

export const PageSizeSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const PageSizeLabel = styled.span`
  font-size: 0.875rem;
  color: ${Theme.colors.neutrals_200};
  white-space: nowrap;
`;

export const PageSizeSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid ${Theme.colors.neutrals_400};
  border-radius: ${Theme.radii.corner_radius_s};
  background: ${Theme.colors.neutrals_500};
  color: ${Theme.colors.neutrals_100};
  cursor: pointer;
  font-size: 0.875rem;

  &:hover {
    border-color: ${Theme.colors.primaries_000};
  }

  &:focus {
    outline: none;
    border-color: ${Theme.colors.primaries_000};
  }
`;

export const PageSizeInput = styled.input`
  padding: 0.5rem;
  border: 1px solid ${Theme.colors.neutrals_400};
  border-radius: ${Theme.radii.corner_radius_s};
  background: ${Theme.colors.neutrals_500};
  color: ${Theme.colors.neutrals_100};
  cursor: text;
  font-size: 0.875rem;
  width: 5rem;
  text-align: center;

  &:hover {
    border-color: ${Theme.colors.primaries_000};
  }

  &:focus {
    outline: none;
    border-color: ${Theme.colors.primaries_000};
  }

  /* Remove spinner arrows */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

export const EmptyStateContainer = styled.tr`
  td {
    padding: 3rem 2rem;
    text-align: center;
  }
`;

export const LoadingRow = styled.tr`
  td {
    padding: 0.75rem 1rem;
  }
`;

export const SkeletonBar = styled.div`
  height: 1rem;
  background: ${Theme.colors.neutrals_300};
  border-radius: ${Theme.radii.corner_radius_xs};
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;
